import React, { useEffect, useState, useCallback } from 'react';
import adminApi from '../utils/adminApi';
import AdminLogin from './AdminLogin';
import toast from 'react-hot-toast';

const SC = { working:'#10B981', busy:'#F59E0B', broken:'#EF4444', unknown:'#475569' };

function Badge({ label, color='var(--teal)', bg }) {
  return <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:10, background: bg||color+'22', color, border:`1px solid ${color}44`, letterSpacing:'0.3px' }}>{label}</span>;
}

function KpiCard({ icon, label, value, sub, color='var(--teal)' }) {
  return (
    <div style={{ background:'var(--slate)', border:'1px solid var(--border)', borderRadius:14, padding:'18px 16px', display:'flex', gap:14, alignItems:'flex-start' }}>
      <div style={{ width:40, height:40, borderRadius:11, background:color+'18', display:'flex', alignItems:'center', justifyContent:'center', fontSize:19, flexShrink:0 }}>{icon}</div>
      <div>
        <div style={{ fontSize:10, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:4 }}>{label}</div>
        <div style={{ fontSize:26, fontWeight:800, color, fontFamily:'JetBrains Mono,monospace', lineHeight:1 }}>{value}</div>
        {sub && <div style={{ fontSize:11, color:'var(--text-3)', marginTop:3 }}>{sub}</div>}
      </div>
    </div>
  );
}

const TABS = [
  { id:'overview',  icon:'📊', label:'Overview'  },
  { id:'revenue',   icon:'💰', label:'Revenue'   },
  { id:'reports',   icon:'📋', label:'Reports'   },
  { id:'chargers',  icon:'⚡', label:'Chargers'  },
  { id:'countries', icon:'🌍', label:'Countries' },
  { id:'operators', icon:'🏢', label:'Operators' },
  { id:'claims',    icon:'🔗', label:'Claims'    },
  { id:'users',     icon:'👥', label:'Users'     },
];

export default function AdminPage() {
  const [authed,         setAuthed]         = useState(!!sessionStorage.getItem('evapp_admin_key'));
  const [stats,          setStats]          = useState(null);
  const [countries,      setCountries]      = useState([]);
  const [claims,         setClaims]         = useState([]);
  const [operators,      setOperators]      = useState([]);
  const [users,          setUsers]          = useState([]);
  const [reports,        setReports]        = useState([]);
  const [chargerSearch,  setChargerSearch]  = useState('');
  const [chargerResults, setChargerResults] = useState([]);
  const [userSearch,     setUserSearch]     = useState('');
  const [loading,        setLoading]        = useState(true);
  const [syncing,        setSyncing]        = useState({});
  const [tab,            setTab]            = useState('overview');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, cl, op] = await Promise.all([
        adminApi.get('/admin/stats'),
        adminApi.get('/admin/countries'),
        adminApi.get('/admin/ownership-claims'),
        adminApi.get('/admin/operators'),
      ]);
      setStats(s.data); setCountries(c.data.countries);
      setClaims(cl.data.claims); setOperators(op.data.operators);
    } catch (_) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (authed) loadAll(); }, [authed, loadAll]);
  if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;

  const loadReports = async () => {
    try { const r = await adminApi.get('/admin/reports'); setReports(r.data.reports||[]); }
    catch (_) { toast.error('Failed'); }
  };

  const handleTabChange = (t) => {
    setTab(t);
    if (t==='reports' && reports.length===0) loadReports();
  };

  const toggleCountry = async (code, enabled) => {
    try { await adminApi.patch(`/admin/countries/${code}`, { enabled: !enabled }); toast.success(!enabled?`${code} sync started`:`${code} disabled`); loadAll(); }
    catch (_) { toast.error('Failed'); }
  };

  const triggerSync = async (code) => {
    setSyncing(s => ({ ...s, [code]: true }));
    try { await adminApi.post(`/admin/sync/${code}`); toast.success(`Sync started for ${code}`); }
    catch (_) { toast.error('Sync failed'); }
    finally { setTimeout(() => setSyncing(s => ({ ...s, [code]: false })), 5000); }
  };

  const reviewClaim = async (id, action) => {
    try { const r = await adminApi.post(`/admin/ownership-claims/${id}/${action}`); toast.success(r.data.message); loadAll(); }
    catch (_) { toast.error('Failed'); }
  };

  const searchChargers = async () => {
    if (chargerSearch.trim().length < 2) return;
    try { const r = await adminApi.get('/admin/chargers/search', { params: { q: chargerSearch } }); setChargerResults(r.data.chargers); }
    catch (_) { toast.error('Failed'); }
  };

  const linkCharger = async (id, opId) => {
    try { await adminApi.patch(`/admin/chargers/${id}/operator`, { operator_id: opId||null }); toast.success('Linked!'); searchChargers(); }
    catch (_) { toast.error('Failed'); }
  };

  const searchUsers = async () => {
    try { const r = await adminApi.get('/admin/users', { params: { search: userSearch } }); setUsers(r.data.users); }
    catch (_) { toast.error('Failed'); }
  };

  const deleteReport = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    try { await adminApi.delete(`/admin/reports/${id}`); toast.success('Deleted'); loadReports(); }
    catch (_) { toast.error('Failed'); }
  };

  const togglePremium = async (userId, current) => {
    try { await adminApi.patch(`/admin/users/${userId}`, { premium: !current }); toast.success(!current?'Premium granted':'Premium revoked'); searchUsers(); }
    catch (_) { toast.error('Failed'); }
  };

  const total = stats?.total_chargers || 0;
  const btnGhost   = { padding:'7px 14px', borderRadius:8, border:'1px solid var(--border-2)', background:'none', color:'var(--text-3)', fontSize:12, fontWeight:500, cursor:'pointer' };
  const btnPrimary = { padding:'8px 16px', borderRadius:8, border:'none', background:'var(--teal)', color:'#F0F4FF', fontSize:13, fontWeight:600, cursor:'pointer' };
  const inputStyle = { flex:1, padding:'9px 12px', borderRadius:8, background:'var(--navy)', border:'1px solid var(--border-2)', color:'var(--text-1)', fontSize:13, fontFamily:'Inter,sans-serif' };
  const emptyBox   = { background:'var(--slate)', border:'1px solid var(--border)', borderRadius:12, padding:'40px', textAlign:'center', color:'var(--text-3)', fontSize:14 };

  return (
    <div style={{ minHeight:'100vh', background:'var(--navy)', fontFamily:'Inter,sans-serif' }}>
      <div style={{ height:56, background:'rgba(255,255,255,0.98)', backdropFilter:'blur(16px)', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 24px', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:28, height:28, borderRadius:8, background:'linear-gradient(135deg,#00D4AA,#0099FF)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14 }}>⚡</div>
          <span style={{ fontWeight:700, fontSize:15 }}>Charge<span style={{ color:'var(--teal)' }}>Sure</span><span style={{ color:'var(--text-3)', fontWeight:400, fontSize:13 }}> · Admin</span></span>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={loadAll} style={btnGhost}>↻ Refresh</button>
          <button onClick={() => { sessionStorage.removeItem('evapp_admin_key'); setAuthed(false); }} style={btnGhost}>Log out</button>
        </div>
      </div>

      <div style={{ maxWidth:1100, margin:'0 auto', padding:'28px 20px 60px' }}>
        {loading ? <div style={{ textAlign:'center', padding:80, color:'var(--text-3)' }}>Loading…</div> : (
          <>
            <div style={{ display:'flex', gap:2, marginBottom:28, borderBottom:'1px solid var(--border)', overflowX:'auto' }}>
              {TABS.map(t => (
                <button key={t.id} onClick={() => handleTabChange(t.id)} style={{ padding:'9px 14px', border:'none', borderRadius:'8px 8px 0 0', background:tab===t.id?'var(--slate)':'transparent', color:tab===t.id?'var(--text-1)':'var(--text-3)', fontSize:13, fontWeight:500, cursor:'pointer', borderBottom:tab===t.id?'2px solid var(--teal)':'2px solid transparent', marginBottom:-1, position:'relative', whiteSpace:'nowrap' }}>
                  {t.icon} {t.label}
                  {t.id==='claims' && claims.length>0 && <span style={{ position:'absolute', top:6, right:6, width:7, height:7, borderRadius:'50%', background:'var(--amber)' }} />}
                </button>
              ))}
            </div>

            {tab==='overview' && stats && (
              <>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:10, marginBottom:24 }}>
                  <KpiCard icon="⚡" label="Total Chargers"  value={total.toLocaleString()}                          color="var(--teal)"  sub="across all countries" />
                  <KpiCard icon="👥" label="Total Users"     value={Number(stats.users?.total||0).toLocaleString()}  color="#818CF8" />
                  <KpiCard icon="⭐" label="Premium Users"   value={Number(stats.users?.premium||0).toLocaleString()} color="var(--amber)" sub="paid subscribers" />
                  <KpiCard icon="📋" label="Reports (24h)"   value={stats.reports?.total_24h||0}                     color="#10B981"       sub={`${stats.reports?.last_hour||0} last hour`} />
                  <KpiCard icon="💳" label="Stripe Subs"     value={stats.revenue?.stripe_subs||0}                   color="var(--teal)" />
                  <KpiCard icon="₹"  label="Razorpay Subs"  value={stats.revenue?.razorpay_subs||0}                 color="var(--teal)" />
                </div>
                <div style={{ background:'var(--slate)', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden', marginBottom:20 }}>
                  <div style={{ padding:'14px 18px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between' }}>
                    <span style={{ fontWeight:600, fontSize:14 }}>Charger Status Breakdown</span>
                    <span style={{ fontSize:12, color:'var(--text-3)' }}>{total.toLocaleString()} total</span>
                  </div>
                  <div style={{ padding:'16px 18px', display:'flex', flexDirection:'column', gap:10 }}>
                    {stats.chargers?.map(c => {
                      const pct = total>0 ? Math.round((c.count/total)*100) : 0;
                      return (
                        <div key={c.status}>
                          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                              <div style={{ width:8, height:8, borderRadius:'50%', background:SC[c.status]||SC.unknown }} />
                              <span style={{ fontSize:13, color:'var(--text-2)', textTransform:'capitalize' }}>{c.status}</span>
                            </div>
                            <span style={{ fontSize:13, fontFamily:'JetBrains Mono,monospace', color:SC[c.status]||'var(--text-2)' }}>
                              {Number(c.count).toLocaleString()} <span style={{ color:'var(--text-3)', fontFamily:'Inter' }}>({pct}%)</span>
                            </span>
                          </div>
                          <div style={{ height:6, background:'var(--navy)', borderRadius:3, overflow:'hidden' }}>
                            <div style={{ height:'100%', width:`${pct}%`, background:SC[c.status]||SC.unknown, borderRadius:3 }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div style={{ background:'var(--slate)', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden' }}>
                  <div style={{ padding:'14px 18px', borderBottom:'1px solid var(--border)', fontWeight:600, fontSize:14 }}>Country Overview</div>
                  {countries.map((c,i) => (
                    <div key={c.country} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 18px', borderBottom:i<countries.length-1?'1px solid var(--border)':'none', flexWrap:'wrap', gap:8 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ width:8, height:8, borderRadius:'50%', background:c.enabled?'#10B981':'var(--text-3)' }} />
                        <span style={{ fontWeight:500, fontSize:13 }}>{c.name}</span>
                        <Badge label={c.country} color="var(--text-3)" />
                      </div>
                      <div style={{ display:'flex', gap:20, alignItems:'center' }}>
                        <span style={{ fontSize:13, fontFamily:'JetBrains Mono,monospace', color:'var(--text-2)' }}>{(c.total_chargers||0).toLocaleString()}</span>
                        <span style={{ fontSize:11, color:'var(--text-3)' }}>{c.last_sync_at?new Date(c.last_sync_at).toLocaleDateString():'Never'}</span>
                        <Badge label={c.enabled?'ACTIVE':'OFF'} color={c.enabled?'var(--teal)':'var(--text-3)'} />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {tab==='revenue' && (
              <>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:10, marginBottom:24 }}>
                  <KpiCard icon="💳" label="Stripe Subs"      value={stats?.revenue?.stripe_subs||0}    color="var(--teal)"  sub="active monthly" />
                  <KpiCard icon="₹"  label="Razorpay Subs"   value={stats?.revenue?.razorpay_subs||0}  color="#818CF8"      sub="active monthly" />
                  <KpiCard icon="📱" label="Play Store Subs"  value={stats?.revenue?.play_subs||0}      color="var(--amber)" sub="active monthly" />
                  <KpiCard icon="⭐" label="Total Premium"    value={stats?.users?.premium||0}           color="var(--teal)"  sub="all platforms" />
                </div>
                <div style={{ background:'var(--slate)', border:'1px solid var(--border)', borderRadius:14, padding:24, marginBottom:20 }}>
                  <div style={{ fontWeight:600, fontSize:14, marginBottom:16 }}>Estimated Monthly Revenue</div>
                  {[
                    { platform:'Stripe (Web)', subs:stats?.revenue?.stripe_subs||0, price:3.99, currency:'€', color:'var(--teal)' },
                    { platform:'Razorpay (India)', subs:stats?.revenue?.razorpay_subs||0, price:99, currency:'₹', color:'#818CF8' },
                    { platform:'Play Store', subs:stats?.revenue?.play_subs||0, price:3.99, currency:'€', color:'var(--amber)' },
                  ].map(({ platform, subs, price, currency, color }) => (
                    <div key={platform} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 0', borderBottom:'1px solid var(--border)' }}>
                      <div>
                        <div style={{ fontWeight:500, fontSize:13 }}>{platform}</div>
                        <div style={{ fontSize:11, color:'var(--text-3)' }}>{subs} subscribers × {currency}{price}/mo</div>
                      </div>
                      <div style={{ fontSize:20, fontWeight:800, color, fontFamily:'JetBrains Mono,monospace' }}>{currency}{(subs*price).toFixed(2)}</div>
                    </div>
                  ))}
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:14, marginTop:4 }}>
                    <span style={{ fontWeight:700, fontSize:14 }}>Total MRR</span>
                    <span style={{ fontSize:24, fontWeight:900, color:'var(--teal)', fontFamily:'JetBrains Mono,monospace' }}>€{(((stats?.revenue?.stripe_subs||0)+(stats?.revenue?.play_subs||0))*3.99).toFixed(2)}</span>
                  </div>
                </div>
                <div style={{ background:'var(--slate)', border:'1px solid var(--border)', borderRadius:14, padding:24 }}>
                  <div style={{ fontWeight:600, fontSize:14, marginBottom:12 }}>Integration Status</div>
                  {[
                    { name:'Stripe', desc:'Web premium billing', active: (stats?.revenue?.stripe_subs||0) > 0 },
                    { name:'Razorpay', desc:'India billing', active: (stats?.revenue?.razorpay_subs||0) > 0 },
                    { name:'Google Play', desc:'Android in-app billing', active: (stats?.revenue?.play_subs||0) > 0 },
                    { name:'OneSignal', desc:'Push notifications', active: false },
                    { name:'SMTP', desc:'Password reset emails', active: false },
                  ].map(({ name, desc, active }) => (
                    <div key={name} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
                      <div>
                        <div style={{ fontWeight:500, fontSize:13 }}>{name}</div>
                        <div style={{ fontSize:11, color:'var(--text-3)' }}>{desc}</div>
                      </div>
                      <Badge label={active?'ACTIVE':'NOT CONFIGURED'} color={active?'var(--teal)':'var(--amber)'} />
                    </div>
                  ))}
                </div>
              </>
            )}

            {tab==='reports' && (
              <>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                  <div style={{ fontSize:14, fontWeight:700 }}>Recent Community Reports</div>
                  <button onClick={loadReports} style={btnGhost}>↻ Refresh</button>
                </div>
                {reports.length===0 ? <div style={emptyBox}>No reports yet.</div> : (
                  <div style={{ background:'var(--slate)', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 120px 80px 90px 70px', padding:'10px 16px', borderBottom:'1px solid var(--border)', fontSize:10, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.5px', gap:12 }}>
                      <span>Charger</span><span>Reporter</span><span>Status</span><span>Date</span><span>Action</span>
                    </div>
                    {reports.map((r,i) => (
                      <div key={r.id} style={{ display:'grid', gridTemplateColumns:'1fr 120px 80px 90px 70px', padding:'12px 16px', borderBottom:i<reports.length-1?'1px solid var(--border)':'none', fontSize:13, gap:12, alignItems:'center' }}>
                        <div>
                          <div style={{ fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.charger_name||'Unknown'}</div>
                          <div style={{ fontSize:11, color:'var(--text-3)' }}>{r.city}, {r.country}</div>
                        </div>
                        <span style={{ fontSize:12, color:'var(--text-3)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.reporter_name||'Anonymous'}</span>
                        <Badge label={(r.status||'').toUpperCase()} color={SC[r.status]||'var(--text-3)'} />
                        <span style={{ fontSize:11, color:'var(--text-3)' }}>{new Date(r.created_at).toLocaleDateString()}</span>
                        <button onClick={() => deleteReport(r.id)} style={{ padding:'4px 8px', borderRadius:6, border:'1px solid #EF444444', background:'none', color:'#EF4444', fontSize:11, cursor:'pointer' }}>Delete</button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {tab==='chargers' && (
              <>
                <div style={{ background:'var(--slate)', border:'1px solid var(--border)', borderRadius:14, padding:20, marginBottom:20 }}>
                  <div style={{ fontSize:13, fontWeight:600, marginBottom:12 }}>Search & Manage Chargers</div>
                  <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                    <input placeholder="Search charger by name…" value={chargerSearch} onChange={e => setChargerSearch(e.target.value)} onKeyDown={e => e.key==='Enter' && searchChargers()} style={inputStyle} />
                    <button onClick={searchChargers} style={btnPrimary}>Search</button>
                  </div>
                  {chargerResults.length>0 && (
                    <div style={{ border:'1px solid var(--border)', borderRadius:10, overflow:'hidden' }}>
                      {chargerResults.map((c,i) => (
                        <div key={c.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', borderBottom:i<chargerResults.length-1?'1px solid var(--border)':'none', background:'var(--navy)', flexWrap:'wrap', gap:8 }}>
                          <div>
                            <div style={{ fontWeight:500, fontSize:13 }}>{c.name}</div>
                            <div style={{ fontSize:11, color:'var(--text-3)' }}>{c.city}, {c.country}</div>
                          </div>
                          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                            <Badge label={(c.status||'').toUpperCase()} color={SC[c.status]||'var(--text-3)'} />
                            <select defaultValue={c.operator_id||''} onChange={e => linkCharger(c.id, e.target.value)} style={{ padding:'5px 8px', borderRadius:7, fontSize:12, background:'var(--slate)', border:'1px solid var(--border-2)', color:'var(--text-1)', cursor:'pointer' }}>
                              <option value="">— No operator —</option>
                              {operators.map(op => <option key={op.id} value={op.id}>{op.name}</option>)}
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:10 }}>
                  {stats?.chargers?.map(c => (
                    <div key={c.status} style={{ background:'var(--slate)', border:`1px solid ${SC[c.status]||'var(--border)'}44`, borderRadius:12, padding:16, textAlign:'center' }}>
                      <div style={{ fontSize:28, fontWeight:800, color:SC[c.status]||'var(--text-1)', fontFamily:'JetBrains Mono,monospace' }}>{Number(c.count).toLocaleString()}</div>
                      <div style={{ fontSize:12, color:'var(--text-3)', textTransform:'capitalize', marginTop:4 }}>{c.status}</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {tab==='countries' && (
              <>
                <p style={{ fontSize:13, color:'var(--text-3)', marginBottom:16 }}>Enable a country to start nightly Open Charge Map sync.</p>
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {countries.map(c => (
                    <div key={c.country} style={{ background:'var(--slate)', border:`1px solid ${c.enabled?'var(--teal-mid)':'var(--border)'}`, borderRadius:13, padding:'16px 18px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
                      <div>
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5 }}>
                          <span style={{ fontWeight:600, fontSize:15 }}>{c.name}</span>
                          <Badge label={c.country} color="var(--text-3)" />
                          <Badge label={c.enabled?'ACTIVE':'OFF'} color={c.enabled?'var(--teal)':'var(--text-3)'} />
                        </div>
                        <div style={{ fontSize:12, color:'var(--text-3)', display:'flex', gap:16, flexWrap:'wrap' }}>
                          <span>⚡ {(c.total_chargers||0).toLocaleString()} chargers</span>
                          <span>Grid: {c.grid_size_deg}°</span>
                          <span>Last sync: {c.last_sync_at?new Date(c.last_sync_at).toLocaleString():'Never'}</span>
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:8 }}>
                        {c.enabled && <button onClick={() => triggerSync(c.country)} disabled={syncing[c.country]} style={btnPrimary}>{syncing[c.country]?'⟳ Running…':'↻ Sync Now'}</button>}
                        <button onClick={() => toggleCountry(c.country, c.enabled)} style={{ ...btnGhost, color:c.enabled?'#EF4444':'var(--teal)', borderColor:c.enabled?'#EF444444':'var(--teal-mid)' }}>{c.enabled?'Disable':'Enable'}</button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {tab==='operators' && (
              <>
                <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:24 }}>
                  {operators.length===0 ? <div style={emptyBox}>No operators registered yet.</div> : operators.map(op => (
                    <div key={op.id} style={{ background:'var(--slate)', border:'1px solid var(--border)', borderRadius:12, padding:'14px 18px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
                      <div>
                        <div style={{ fontWeight:600, fontSize:14, marginBottom:3 }}>{op.name}</div>
                        <div style={{ fontSize:12, color:'var(--text-3)', display:'flex', gap:12 }}>
                          <span>⚡ {op.charger_count||0} chargers</span>
                          {op.country && <span>📍 {op.country}</span>}
                        </div>
                      </div>
                      <Badge label={op.plan==='none'?'FREE':`${op.plan.toUpperCase()} · ${op.plan_status}`} color={op.plan!=='none'?'var(--teal)':'var(--text-3)'} />
                    </div>
                  ))}
                </div>
                <div style={{ background:'var(--slate)', border:'1px solid var(--border)', borderRadius:14, padding:20 }}>
                  <div style={{ fontSize:13, fontWeight:600, marginBottom:12 }}>Link Charger to Operator</div>
                  <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                    <input placeholder="Charger name…" value={chargerSearch} onChange={e => setChargerSearch(e.target.value)} onKeyDown={e => e.key==='Enter' && searchChargers()} style={inputStyle} />
                    <button onClick={searchChargers} style={btnPrimary}>Search</button>
                  </div>
                  {chargerResults.length>0 && (
                    <div style={{ border:'1px solid var(--border)', borderRadius:10, overflow:'hidden' }}>
                      {chargerResults.map((c,i) => (
                        <div key={c.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', borderBottom:i<chargerResults.length-1?'1px solid var(--border)':'none', background:'var(--navy)', flexWrap:'wrap', gap:8 }}>
                          <div>
                            <div style={{ fontWeight:500, fontSize:13 }}>{c.name}</div>
                            <div style={{ fontSize:11, color:'var(--text-3)' }}>{c.city}, {c.country}</div>
                          </div>
                          <select defaultValue={c.operator_id||''} onChange={e => linkCharger(c.id, e.target.value)} style={{ padding:'5px 8px', borderRadius:7, fontSize:12, background:'var(--slate)', border:'1px solid var(--border-2)', color:'var(--text-1)', cursor:'pointer' }}>
                            <option value="">— No operator —</option>
                            {operators.map(op => <option key={op.id} value={op.id}>{op.name}</option>)}
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {tab==='claims' && (
              <>
                <p style={{ fontSize:13, color:'var(--text-3)', marginBottom:16 }}>Operators self-register and submit a claim. Approval auto-links chargers by name match.</p>
                {claims.length===0 ? <div style={emptyBox}>✓ No pending claims.</div> : claims.map(c => (
                  <div key={c.id} style={{ background:'var(--slate)', border:'1px solid #F59E0B44', borderRadius:12, padding:'16px 18px', marginBottom:10 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10 }}>
                      <div>
                        <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>
                          <span style={{ color:'var(--teal)' }}>{c.operator_company_name}</span>
                          <span style={{ color:'var(--text-3)', fontWeight:400 }}> claims to be </span>"{c.claimed_name}"
                        </div>
                        {c.note && <div style={{ fontSize:13, color:'var(--text-2)', marginBottom:6 }}>{c.note}</div>}
                        <div style={{ fontSize:11, color:'var(--text-3)' }}>{new Date(c.created_at).toLocaleString()}</div>
                      </div>
                      <div style={{ display:'flex', gap:8 }}>
                        <button onClick={() => reviewClaim(c.id,'approve')} style={btnPrimary}>Approve</button>
                        <button onClick={() => reviewClaim(c.id,'reject')} style={{ ...btnGhost, color:'#EF4444', borderColor:'#EF444444' }}>Reject</button>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {tab==='users' && (
              <>
                <div style={{ display:'flex', gap:8, marginBottom:16 }}>
                  <input placeholder="Search by email or name…" value={userSearch} onChange={e => setUserSearch(e.target.value)} onKeyDown={e => e.key==='Enter' && searchUsers()} style={inputStyle} />
                  <button onClick={searchUsers} style={btnPrimary}>Search</button>
                </div>
                {users.length===0 ? <div style={emptyBox}>Search for users above.</div> : (
                  <div style={{ background:'var(--slate)', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 80px 80px 60px 60px 80px', padding:'10px 16px', borderBottom:'1px solid var(--border)', fontSize:10, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.5px', gap:8 }}>
                      <span>User</span><span>Country</span><span>Plan</span><span>Reports</span><span>Points</span><span>Action</span>
                    </div>
                    {users.map((u,i) => (
                      <div key={u.id} style={{ display:'grid', gridTemplateColumns:'1fr 80px 80px 60px 60px 80px', padding:'12px 16px', borderBottom:i<users.length-1?'1px solid var(--border)':'none', alignItems:'center', gap:8 }}>
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontWeight:500, fontSize:13, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.name||'—'}</div>
                          <div style={{ fontSize:11, color:'var(--text-3)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.email}</div>
                        </div>
                        <span style={{ fontSize:12, color:'var(--text-3)' }}>{u.country||'—'}</span>
                        <Badge label={u.premium?'PRO':'FREE'} color={u.premium?'var(--teal)':'var(--text-3)'} />
                        <span style={{ fontSize:13, fontFamily:'JetBrains Mono,monospace', color:'var(--text-2)' }}>{u.report_count}</span>
                        <span style={{ fontSize:13, fontFamily:'JetBrains Mono,monospace', color:'var(--teal)' }}>{u.points}</span>
                        <button onClick={() => togglePremium(u.id, u.premium)} style={{ padding:'4px 8px', borderRadius:6, border:`1px solid ${u.premium?'#EF444444':'var(--teal-mid)'}`, background:'none', color:u.premium?'#EF4444':'var(--teal)', fontSize:11, cursor:'pointer' }}>
                          {u.premium?'Revoke':'Grant'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
